import React from 'react'
import { Button, CheckPicker, Panel, Tag } from 'rsuite'
import {
  DndContext,
  DragEndEvent,
  closestCenter
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import SortIcon from '@rsuite/icons/Sort'
import { ArrowUpOutline, ArrowDownOutline, CloseOutline } from 'react-ionicons'

type ProviderOption = {
  label: string
  value: string
}

type SortableProviderPickerProps = {
  data: ProviderOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
}

export default function SortableProviderPicker ({
  data,
  value,
  onChange,
  placeholder = 'Выберите провайдеров'
}: SortableProviderPickerProps) {

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && value) {
      const oldIndex = value.findIndex((v) => v === active.id)
      const newIndex = value.findIndex((v) => v === over?.id)
      const newOrder = arrayMove(value, oldIndex, newIndex)
      onChange(newOrder)
    }
  }

  const handleRemove = (providerValue: string) => {
    onChange(value.filter((v) => v !== providerValue))
  }

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newOrder = arrayMove(value, index, index - 1)
      onChange(newOrder)
    }
  }

  const handleMoveDown = (index: number) => {
    if (index < value.length - 1) {
      const newOrder = arrayMove(value, index, index + 1)
      onChange(newOrder)
    }
  }

  const getProviderLabel = (providerValue: string) => {
    return data.find((d) => d.value === providerValue)?.label ?? providerValue
  }

  return (
    <div>
      <CheckPicker
        data={data}
        value={value}
        onChange={onChange}
        labelKey="label"
        valueKey="value"
        placeholder={placeholder}
        block
      />
      
      {value.length > 0 && (
        <div className="mt-2">
          <small className="text-muted mb-1 d-block">
            <SortIcon style={{ marginRight: 4 }} />
            Перетаскивайте для изменения порядка:
          </small>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={value} strategy={verticalListSortingStrategy}>
              {value.map((providerValue, index) => (
                <SortableProviderItem
                  key={providerValue}
                  id={providerValue}
                  label={getProviderLabel(providerValue)}
                  index={index}
                  total={value.length}
                  onRemove={() => handleRemove(providerValue)}
                  onMoveUp={() => handleMoveUp(index)}
                  onMoveDown={() => handleMoveDown(index)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  )
}

type SortableProviderItemProps = {
  id: string
  label: string
  index: number
  total: number
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

function SortableProviderItem ({
  id,
  label,
  index,
  total,
  onRemove,
  onMoveUp,
  onMoveDown
}: SortableProviderItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    marginBottom: 4,
    backgroundColor: '#f7f7fa',
    borderRadius: 6,
    border: '1px solid #e5e5ea',
    cursor: 'grab'
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tag color="blue" size="sm">{index + 1}</Tag>
        <span>{label}</span>
      </div>
      <div 
        style={{ display: 'flex', gap: 4 }} 
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Button
          size="xs"
          appearance="subtle"
          disabled={index === 0}
          onClick={onMoveUp}
        >
          <ArrowUpOutline width="14px" height="14px" />
        </Button>
        <Button
          size="xs"
          appearance="subtle"
          disabled={index === total - 1}
          onClick={onMoveDown}
        >
          <ArrowDownOutline width="14px" height="14px" />
        </Button>
        <Button
          size="xs"
          appearance="subtle"
          color="red"
          onClick={onRemove}
        >
          <CloseOutline width="14px" height="14px" />
        </Button>
      </div>
    </div>
  )
}
